import { Badge, type BadgeVariant } from '../Badge/Badge';

export interface StatusBadgeProps<T extends string> {
  status: T | null | undefined;
  /** Domain-specific status → variant mapping. Required so this component
   *  doesn't hardcode booking/receipt/pending status names. */
  statusMap: Record<T, BadgeVariant>;
  /** Optional pretty-printer. Defaults to humanising the status string
   *  (snake_case → Title Case). */
  formatLabel?: (status: T) => string;
  /** Fallback variant when the status isn't in the map. */
  fallback?: BadgeVariant;
  /** Label rendered when status is null/undefined/empty. */
  unknownLabel?: string;
  /** Variant rendered when status is null/undefined/empty. */
  unknownVariant?: BadgeVariant;
}

function defaultLabel(status: string): string {
  if (!status) return '';
  return status
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function StatusBadge<T extends string>({
  status,
  statusMap,
  formatLabel,
  fallback = 'default',
  unknownLabel = 'Unknown',
  unknownVariant = 'muted',
}: StatusBadgeProps<T>) {
  if (status == null || status === '') {
    return <Badge variant={unknownVariant}>{unknownLabel}</Badge>;
  }
  const variant = statusMap[status] ?? fallback;
  const label = formatLabel ? formatLabel(status) : defaultLabel(status);
  return <Badge variant={variant}>{label}</Badge>;
}
